import { useEffect, useRef, useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'
import { useUserStore } from '../stores'
import { unsubscribePushOnServer } from '../services/push'
import { stopNativePush } from '../services/nativePush'
import { idleStartAt, shouldStampActivity } from '../utils/sessionReturn'
import { readIdleStamp, writeIdleStamp, clearIdleStamp } from '../utils/idleStamp'

const IDLE_TIMEOUT_MS = 30 * 60 * 1000   // 30 minutes of FOREGROUND inactivity
const WARNING_BEFORE_MS = 60 * 1000       // warn for the last 60s of that
const TICK_MS = 1000
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'click']

/**
 * useIdleTimeout — the FOREGROUND inactivity clock, and nothing else.
 *
 * ★★★ THIS HOOK IS ABOUT A USER WHO IS LOOKING AT THE APP AND NOT TOUCHING IT.
 *     IT IS NOT ABOUT A USER WHO LEFT. The away/return decision — continue, PIN
 *     lock, or full re-login — belongs to usePrivacyScreen, which is the only
 *     thing that knows how long the app was actually backgrounded. Two owners
 *     for one question is how they disagree, and they did.
 *
 * ★★★ WHY IT IS A WALL-CLOCK POLL AND NOT A setTimeout. It used to arm one
 *     29-minute setTimeout and trust it. iOS suspends JS timers when the app
 *     backgrounds; on resume WKWebView fires the overdue timer IMMEDIATELY. So
 *     coming back after two hours away raised the inactivity modal with a fresh
 *     60-second countdown and a "Stay Logged In" button — a timer asking the
 *     patient whether they wanted to come back, on the one path where the answer
 *     was supposed to be a PIN or a re-login. The countdown is now DERIVED from
 *     (now - lastActivity) on every tick, so a suspended interval cannot invent
 *     a deadline: a tick that arrives late computes the true elapsed time, and a
 *     tick that never arrives shows nothing.
 *
 * ★★★ AND THE CLOCK IS RESET ON RESUME, NOT EVALUATED ACROSS THE GAP. Time spent
 *     backgrounded is not foreground inactivity, so it must not count toward this
 *     timer at all — usePrivacyScreen has already judged that same gap by the
 *     time we get here, and judging it twice is what produced two different
 *     answers on one return.
 *
 * ★★★ PROD-252 (P17) — AND THE CLOCK SURVIVES A RELOAD AND A REMOUNT. This effect used to
 *     set `lastActivityRef.current = Date.now()` every time it ran and store nothing, so a
 *     reload, and every navigation (MobileLayout remounts under
 *     `<Routes key={location.pathname}>`), handed the patient a fresh 30 minutes. The last
 *     activity and the last hide now live in utils/idleStamp.js, and on every run the effect
 *     asks sessionReturn.idleStartAt where the clock stands BEFORE it polls. An expired or
 *     unreadable stamp sets the clock a full timeout back, so the poll below performs the
 *     timeout on its first tick: one owner for the terminal action. A remount flushes the
 *     exact time on cleanup, so a programmatic navigate is not activity; a real background
 *     (>= RESUME_GRACE_MS) still resets on return (D269), because usePrivacyScreen owns it.
 */
export default function useIdleTimeout() {
  const navigate = useNavigate()
  const logout = useUserStore((s) => s.logout)
  const isAuthenticated = useUserStore((s) => s.isAuthenticated)
  // v239.6.0: when a session PIN is set, the idle timeout LOCKS (quick PIN
  // resume, bounded by the PIN window) instead of forcing a full re-login.
  const lockSession = useUserStore((s) => s.lockSession)
  const pinEnabled = useUserStore((s) => !!s.user?.sessionPinEnabled)
  // v239.6.0: while the PIN lock screen is up it owns the timeout (its own window
  // countdown). The clock pauses so it can't re-fire over the lock.
  const isLocked = useUserStore((s) => s.isLocked)

  const [showWarning, setShowWarning] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(WARNING_BEFORE_MS / 1000)

  const lastActivityRef = useRef(Date.now())
  const tickRef = useRef(null)
  const suspendedRef = useRef(false)
  // Keep latest PIN state available inside the tick without re-arming it.
  const pinEnabledRef = useRef(pinEnabled)
  useEffect(() => { pinEnabledRef.current = pinEnabled }, [pinEnabled])

  const clearTick = useCallback(() => {
    if (tickRef.current) clearInterval(tickRef.current)
    tickRef.current = null
  }, [])

  // Explicit full logout — the modal's "Log Out Now" button.
  const performLogout = useCallback(async () => {
    clearTick()
    setShowWarning(false)
    // PROD-1181: the patient tapped it, so this is an ACTIVE log out: remove this browser's push endpoint
    // at the API while the token is still valid. performTimeout below (automatic) never does.
    try { await unsubscribePushOnServer() } catch (_) {}
    try { await stopNativePush() } catch (_) {}   // s119 (D532): this iPhone's APNs token too
    logout()
    navigate('/signin', { replace: true })
  }, [clearTick, logout, navigate])

  // Terminal action when the FOREGROUND idle period elapses. Lock if a PIN is
  // set (resume within the window), otherwise full logout.
  const performTimeout = useCallback(() => {
    clearTick()
    setShowWarning(false)
    if (pinEnabledRef.current) {
      lockSession(window.location?.pathname || null)
    } else {
      logout()
      navigate('/signin', { replace: true })
    }
  }, [clearTick, lockSession, logout, navigate])

  const stayLoggedIn = useCallback(() => {
    lastActivityRef.current = Date.now()
    writeIdleStamp({ lastActivityAt: lastActivityRef.current, hiddenAt: null })
    setShowWarning(false)
  }, [])

  useEffect(() => {
    if (!isAuthenticated || isLocked) {
      clearTick()
      setShowWarning(false)
      suspendedRef.current = false
      // PROD-252: no foreground clock while signed out or locked; the unlock writes a fresh one.
      clearIdleStamp()
      return
    }

    // PROD-252: where the clock stands, from the stored stamp — never a bare Date.now().
    const mountNow = Date.now()
    const start = idleStartAt({ ...readIdleStamp(), now: mountNow, timeoutMs: IDLE_TIMEOUT_MS })
    lastActivityRef.current = start.expired ? mountNow - IDLE_TIMEOUT_MS : start.startAt
    let hiddenAt = null
    let lastWrittenAt = mountNow
    writeIdleStamp({ lastActivityAt: lastActivityRef.current, hiddenAt: null })
    setShowWarning(false)

    // ★ Derived on every tick from wall-clock elapsed time. Nothing is scheduled
    //   against a deadline, so nothing can fire against a stale one.
    tickRef.current = setInterval(() => {
      if (suspendedRef.current) return
      const elapsed = Date.now() - lastActivityRef.current
      if (elapsed >= IDLE_TIMEOUT_MS) {
        performTimeout()
        return
      }
      if (elapsed >= IDLE_TIMEOUT_MS - WARNING_BEFORE_MS) {
        setShowWarning(true)
        setSecondsLeft(Math.max(0, Math.ceil((IDLE_TIMEOUT_MS - elapsed) / 1000)))
      } else {
        setShowWarning(false)
      }
    }, TICK_MS)

    const handleActivity = () => {
      // While the warning is up the patient must answer it explicitly; a stray
      // scroll behind the modal is not an answer.
      if (suspendedRef.current) return
      lastActivityRef.current = Date.now()
      // PROD-252: stored at most every 15 s; a reload loses at most that much.
      if (shouldStampActivity(lastWrittenAt, lastActivityRef.current)) {
        lastWrittenAt = lastActivityRef.current
        writeIdleStamp({ lastActivityAt: lastActivityRef.current, hiddenAt: null })
      }
    }

    ACTIVITY_EVENTS.forEach((evt) => {
      document.addEventListener(evt, handleActivity, { passive: true })
    })

    // ★★★ THE BACKGROUND GUARD. While the app is away this clock stops, and on
    //     return it restarts from zero rather than judging the gap it did not
    //     watch. usePrivacyScreen owns that gap.
    const goSuspended = () => {
      suspendedRef.current = true
      // PROD-252: the hide is stamped, so a reload inside RESUME_GRACE_MS carries the clock
      //   and a longer background resets it on the next mount (idleStartAt).
      if (hiddenAt === null) hiddenAt = Date.now()
      writeIdleStamp({ lastActivityAt: lastActivityRef.current, hiddenAt })
      setShowWarning(false)
    }
    const goResumed = () => {
      suspendedRef.current = false
      lastActivityRef.current = Date.now()
      hiddenAt = null
      // D269: a return is still a reset; stored so a reload right after it agrees.
      lastWrittenAt = lastActivityRef.current
      writeIdleStamp({ lastActivityAt: lastActivityRef.current, hiddenAt: null })
      setShowWarning(false)
    }
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') goSuspended()
      else goResumed()
    }
    document.addEventListener('visibilitychange', handleVisibility)

    let cancelled = false
    let appHandle = null
    if (Capacitor.isNativePlatform && Capacitor.isNativePlatform()) {
      import('@capacitor/app').then(({ App }) => {
        if (cancelled) return
        App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) goResumed()
          else goSuspended()
        }).then((h) => {
          if (cancelled) h.remove()
          else appHandle = h
        })
      }).catch(() => {})
    }

    return () => {
      cancelled = true
      clearTick()
      // PROD-252: a remount (every navigation) keeps the exact time. Only while the session is
      //   still live: a logout or a lock has cleared the stamp, and this must not undo it.
      const live = useUserStore.getState()
      if (live.isAuthenticated && !live.isLocked) writeIdleStamp({ lastActivityAt: lastActivityRef.current, hiddenAt })
      ACTIVITY_EVENTS.forEach((evt) => {
        document.removeEventListener(evt, handleActivity)
      })
      document.removeEventListener('visibilitychange', handleVisibility)
      if (appHandle) appHandle.remove()
    }
  }, [isAuthenticated, isLocked, clearTick, performTimeout])

  return { showWarning, secondsLeft, stayLoggedIn, performLogout, willLock: pinEnabled }
}
